import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  getAllShippingMethods,
  getShippingMethodById,
  createShippingMethod,
  updateShippingMethod,
  deleteShippingMethod,
  reset,
  clearCurrentMethod,
} from "../store/slices/adminShippingSlice";

/**
 * Custom hook for managing shipping methods
 * @returns {Object} Shipping methods state and handlers
 */
const useShippingMethods = () => {
  const { t } = useTranslation("hooks");
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const {
    shippingMethods,
    currentMethod,
    isLoading,
    isSuccess,
    isError,
    message,
  } = useSelector((state) => state.adminShipping);

  // Local state for dialog and form management
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "0",
    estimatedDeliveryTime: "",
    isActive: true,
    minOrderAmount: "",
    maxOrderAmount: "",
    freeShippingThreshold: "",
    trackingUrlFormat: "",
  });
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch shipping methods
  const fetchShippingMethods = useCallback(() => {
    dispatch(getAllShippingMethods());
  }, [dispatch]);

  // Initial data fetch
  useEffect(() => {
    fetchShippingMethods();
    return () => {
      dispatch(reset());
    };
  }, [fetchShippingMethods, dispatch]);

  // Handle API success and error states
  useEffect(() => {
    if (isError) {
      enqueueSnackbar(message || t("shippingMethods.errors.generic"), {
        variant: "error",
      });
      dispatch(reset());
    }

    if (isSuccess) {
      dispatch(reset());
    }
  }, [isError, isSuccess, message, dispatch, enqueueSnackbar, t]);

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("shippingMethods.validation.nameRequired");
    }

    if (formData.price !== "" && isNaN(Number(formData.price))) {
      newErrors.price = t("shippingMethods.validation.priceInvalid");
    }

    if (
      formData.minOrderAmount !== "" &&
      isNaN(Number(formData.minOrderAmount))
    ) {
      newErrors.minOrderAmount = t("shippingMethods.validation.amountInvalid");
    }

    if (
      formData.maxOrderAmount !== "" &&
      isNaN(Number(formData.maxOrderAmount))
    ) {
      newErrors.maxOrderAmount = t("shippingMethods.validation.amountInvalid");
    }

    if (
      formData.freeShippingThreshold !== "" &&
      isNaN(Number(formData.freeShippingThreshold))
    ) {
      newErrors.freeShippingThreshold = t(
        "shippingMethods.validation.amountInvalid"
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  // Handle form input changes
  const handleInputChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));

      // Clear validation error when field is edited
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: null,
        }));
      }
    },
    [errors]
  );

  // Handle add dialog open
  const handleAddOpen = useCallback(() => {
    setSelectedMethod(null);
    setFormData({
      name: "",
      description: "",
      price: "0",
      estimatedDeliveryTime: "",
      isActive: true,
      minOrderAmount: "",
      maxOrderAmount: "",
      freeShippingThreshold: "",
      trackingUrlFormat: "",
    });
    setOpenDialog(true);
  }, []);

  // Handle edit dialog open
  const handleEditOpen = useCallback((method) => {
    setSelectedMethod(method);
    setFormData({
      name: method.name,
      description: method.description || "",
      price: method.price.toString(),
      estimatedDeliveryTime: method.estimatedDeliveryTime || "",
      isActive: method.isActive,
      minOrderAmount: method.minOrderAmount
        ? method.minOrderAmount.toString()
        : "",
      maxOrderAmount: method.maxOrderAmount
        ? method.maxOrderAmount.toString()
        : "",
      freeShippingThreshold: method.freeShippingThreshold
        ? method.freeShippingThreshold.toString()
        : "",
      trackingUrlFormat: method.trackingUrlFormat || "",
    });
    setOpenDialog(true);
  }, []);

  // Handle dialog close
  const handleClose = useCallback(() => {
    setOpenDialog(false);
    setOpenDeleteDialog(false);
    setErrors({});
    dispatch(clearCurrentMethod());
  }, [dispatch]);

  // Handle delete dialog open
  const handleDeleteOpen = useCallback((method) => {
    setSelectedMethod(method);
    setOpenDeleteDialog(true);
  }, []);

  // Handle saving shipping method
  const handleSave = useCallback(async () => {
    if (!validateForm()) return;

    const methodData = {
      ...formData,
      price: formData.price === "" ? 0 : parseFloat(formData.price),
      minOrderAmount:
        formData.minOrderAmount === ""
          ? null
          : parseFloat(formData.minOrderAmount),
      maxOrderAmount:
        formData.maxOrderAmount === ""
          ? null
          : parseFloat(formData.maxOrderAmount),
      freeShippingThreshold:
        formData.freeShippingThreshold === ""
          ? null
          : parseFloat(formData.freeShippingThreshold),
      trackingUrlFormat: formData.trackingUrlFormat || null,
    };

    // Debug logging for trackingUrlFormat
    console.log("Form data trackingUrlFormat:", formData.trackingUrlFormat);
    console.log("Method data being sent:", methodData);

    try {
      if (selectedMethod) {
        // Update existing method
        console.log("Updating shipping method:", selectedMethod.id);
        console.log("With data:", methodData);
        await dispatch(
          updateShippingMethod({ id: selectedMethod.id, methodData })
        ).unwrap();
        enqueueSnackbar(t("shippingMethods.success.updated"), {
          variant: "success",
        });
      } else {
        // Create new method
        console.log("Creating new shipping method with data:", methodData);
        await dispatch(createShippingMethod(methodData)).unwrap();
        enqueueSnackbar(t("shippingMethods.success.created"), {
          variant: "success",
        });
      }

      handleClose();
      fetchShippingMethods();
    } catch (error) {
      console.error("Error saving shipping method:", error);
      enqueueSnackbar(t("shippingMethods.errors.saveFailed"), {
        variant: "error",
      });
    }
  }, [
    validateForm,
    formData,
    selectedMethod,
    dispatch,
    enqueueSnackbar,
    t,
    handleClose,
    fetchShippingMethods,
  ]);

  // Handle deleting shipping method
  const handleDelete = useCallback(async () => {
    if (!selectedMethod) return;

    try {
      await dispatch(deleteShippingMethod(selectedMethod.id)).unwrap();
      enqueueSnackbar(t("shippingMethods.success.deleted"), {
        variant: "success",
      });
      handleClose();
      fetchShippingMethods();
    } catch (error) {
      console.error("Error deleting shipping method:", error);
      enqueueSnackbar(t("shippingMethods.errors.deleteFailed"), {
        variant: "error",
      });
    }
  }, [
    selectedMethod,
    dispatch,
    enqueueSnackbar,
    t,
    handleClose,
    fetchShippingMethods,
  ]);

  // Handle pagination changes
  const handleChangePage = useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  return {
    // State
    shippingMethods,
    currentMethod,
    isLoading,
    openDialog,
    openDeleteDialog,
    selectedMethod,
    formData,
    errors,
    page,
    rowsPerPage,

    // Handlers
    handleInputChange,
    handleAddOpen,
    handleEditOpen,
    handleClose,
    handleDeleteOpen,
    handleSave,
    handleDelete,
    handleChangePage,
    handleChangeRowsPerPage,
    fetchShippingMethods,
  };
};

export default useShippingMethods;
